import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, CheckCircle, ArrowRight, MessageSquare, Clock, Shield, Users } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import AccountingDemoChat from '@/components/demo/AccountingDemoChat';

const AssistantsAccounting: React.FC = () => {
  const [showDemo, setShowDemo] = useState(false);

  const useCases = [
    {
      icon: MessageSquare,
      title: 'Xero/MYOB Helpdesk',
      description: 'Answer common software questions, guide clients through features, and reduce support calls.',
    },
    {
      icon: Users,
      title: 'Client Onboarding',
      description: 'Walk new clients through document requirements, timelines, and firm processes.',
    },
    {
      icon: Clock,
      title: 'Deadline Reminders',
      description: 'Proactive notifications about GST, PAYE, and annual return deadlines.',
    },
    {
      icon: Shield,
      title: 'Compliance Queries',
      description: 'Answer general questions about tax obligations and compliance requirements.',
    },
  ];

  const benefits = [
    'Reduce repetitive client queries by 60%+',
    'Available 24/7 for client questions',
    'Consistent, accurate messaging every time',
    'Free up staff for high-value advisory work',
    'Built-in NZ tax and compliance context',
    'Seamless handoff to human when needed',
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
                <Calculator className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Advisory Assistants for Accounting
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Governed AI assistants designed specifically for accounting firms, 
                bookkeepers, and financial advisors in New Zealand.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/assistants/get-assistant">
                  <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90 w-full sm:w-auto">
                    Get Your Assistant
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => setShowDemo(true)}
                >
                  <MessageSquare className="mr-2 h-5 w-5" />
                  Try the Accounting Assistant (Live Demo)
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
              Built for Accounting Workflows
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Pre-configured templates and governance packs designed for common accounting scenarios.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 hover:border-brand-teal transition-colors">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-teal/10 rounded-lg mb-4">
                    <useCase.icon className="h-6 w-6 text-brand-teal" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-card-foreground">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                Why Accounting Firms Choose Us
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 bg-card p-4 rounded-lg border border-border">
                    <CheckCircle className="h-6 w-6 text-brand-teal flex-shrink-0 mt-0.5" />
                    <span className="text-card-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Governance Link */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-card border border-brand-teal rounded-xl p-8 text-center">
              <Shield className="h-12 w-12 text-brand-teal mx-auto mb-4" />
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">
                Accounting-Specific Governance
              </h3>
              <p className="text-muted-foreground mb-6">
                Our governed AI assistants are built on a Trust Charter that ensures 
                compliance with NZ professional standards and data protection requirements.
              </p>
              <Link to="/assistants/governance/accounting">
                <Button variant="outline">
                  View Trust Charter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-24 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Client Support?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Get your governed AI assistant for accounting deployed in as little as 14 days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/assistants/get-assistant">
                <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => setShowDemo(true)}
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Try the Demo First
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Demo Dialog */}
      <Dialog open={showDemo} onOpenChange={setShowDemo}>
        <DialogContent className="max-w-4xl h-[90vh] p-0 overflow-hidden" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Accounting Advisory Assistant Demo</DialogTitle>
          <AccountingDemoChat onClose={() => setShowDemo(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssistantsAccounting;
